import { ChordLib } from '@tunasong/music-lib'
import type { Chord, NoteEvent } from '@tunasong/music-lib'
import type { NoteName } from '@tunasong/schemas'
import { useEffect, useRef } from 'react'
import { usePlayNotes } from '../player/piano-player.hook.js'

/**
 * Function Quiz v1 audition: two chord stabs (quiz-chord then tonic-chord)
 * separated by GAP_MS. Half-note each, ~2600 ms total at 100 BPM.
 *
 * Reuses Cadence Lab's `chordToEvents` + `setTimeout` scheduling pattern,
 * specialised to the fixed two-stab shape. NOT looping.
 *
 * Spec: docs/superpowers/specs/2026-05-18-function-quiz-design.md §4.4
 */
const TEMPO_BPM = 100
const BEAT_MS = 60_000 / TEMPO_BPM
const HALF_NOTE_MS = BEAT_MS * 2 // 1200ms — quiz chord and tonic each
const GAP_MS = 200 // brief release between chords (Bowie §8.2 verdict)
const CHORD_OCTAVE = 4

export interface QuizStab {
  events: NoteEvent[]
  delayMs: number
}

const chordToEvents = (chord: Chord): NoteEvent[] =>
  ChordLib.getNotes(chord).map(n => ({
    type: 'note' as const,
    note: `${n}${CHORD_OCTAVE}` as NoteName,
    duration: '2n',
  }))

export const buildQuizAuditionStabs = (quizChord: Chord, tonicChord: Chord): QuizStab[] => [
  { events: chordToEvents(quizChord), delayMs: 0 },
  { events: chordToEvents(tonicChord), delayMs: HALF_NOTE_MS + GAP_MS },
]

export interface UseFunctionQuizPlaybackProps {
  quizChord: Chord | null
  tonicChord: Chord | null
}

/**
 * Hook returning `{ play, stop }`. One-shot — no loop. `play` cancels any
 * in-flight audition and starts a fresh one. `stop` cancels in-flight only.
 * Cancel-on-unmount via useEffect cleanup.
 */
export const useFunctionQuizPlayback = (
  props: UseFunctionQuizPlaybackProps
): {
  play: () => void
  stop: () => void
} => {
  const { playNotes } = usePlayNotes()
  const timeoutsRef = useRef<number[]>([])

  const stop = () => {
    timeoutsRef.current.forEach(t => window.clearTimeout(t))
    timeoutsRef.current = []
  }

  const play = () => {
    stop()
    if (!props.quizChord || !props.tonicChord) {
      return
    }
    const stabs = buildQuizAuditionStabs(props.quizChord, props.tonicChord)
    for (const stab of stabs) {
      const handle = window.setTimeout(() => playNotes(stab.events), stab.delayMs)
      timeoutsRef.current.push(handle)
    }
  }

  useEffect(() => stop, [])
  return { play, stop }
}
